import type { Code99Event, Code99Session } from '@/types';

/**
 * Code 99 auto-narrative builder.
 *
 * Per addendum § 1.4, at Code 99 Mode closure the event log passes
 * through a specialized resuscitation prompt. The generated narrative
 * is a chronological clinical account. Every sentence traces back to
 * a logged, timestamped event — nothing invented.
 *
 * This module builds the DRAFT narrative locally (deterministic
 * template filled from the event log). The Claude API narrative
 * engine is optional enrichment — the local draft is itself a
 * medico-legally acceptable record.
 */

function tstr(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour12: false });
}

function durationBetween(a: string, b: string): string {
  const ms = Math.max(0, new Date(b).getTime() - new Date(a).getTime());
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function describeEvent(e: Code99Event): string {
  const t = tstr(e.event_time);
  const d = e.details ?? {};
  const retro = e.retroactive ? ' [retroactive]' : '';
  switch (e.event_type) {
    case 'rhythm':     return `${t}${retro} — Rhythm: ${d.rhythm ?? 'unspecified'}.`;
    case 'shock':      return `${t}${retro} — Defibrillation delivered at ${d.joules ?? '?'} J${d.device_confirmed ? ' (device-confirmed)' : ''}.`;
    case 'cpr_start':  return `${t}${retro} — CPR initiated.`;
    case 'cpr_stop':   return `${t}${retro} — CPR paused${d.reason ? ` (${d.reason})` : ''}.`;
    case 'cpr_resume': return `${t}${retro} — CPR resumed.`;
    case 'airway': {
      const parts: string[] = [String(d.type ?? 'Airway device placed')];
      if (d.size)           parts.push(`size ${d.size}`);
      if (d.depth_at_teeth) parts.push(`depth ${d.depth_at_teeth} cm`);
      if (d.etco2_reading)  parts.push(`EtCO2 ${d.etco2_reading}`);
      if (d.confirmation)   parts.push(String(d.confirmation));
      if (d.attempts)       parts.push(`${d.attempts} attempt(s)`);
      return `${t}${retro} — ${parts.join(', ')}.`;
    }
    case 'medication':
      return `${t}${retro} — ${d.name ?? 'Medication'} ${d.dose ?? ''}${d.unit ?? ''} ${d.route ?? ''} administered.`.replace(/\s+/g, ' ').trim();
    case 'iv_io':
      return `${t}${retro} — ${d.type ?? 'Vascular access'} attempted at ${d.site ?? 'unspecified site'}${d.gauge ? `, ${d.gauge}g` : ''} — ${d.success ? 'successful' : 'unsuccessful'}${d.attempts ? ` (${d.attempts} attempt(s))` : ''}.`;
    case 'twelve_lead':
      return `${t}${retro} — 12-lead ECG acquired${d.stemi ? '; STEMI pattern identified' : ''}${d.interpretation ? `. Interpretation: ${d.interpretation}` : ''}.`;
    case 'rosc':       return `${t}${retro} — Return of spontaneous circulation.`;
    case 'termination':return `${t}${retro} — Resuscitation terminated.`;
    case 'reversible_cause':
      return `${t}${retro} — Reversible cause (${d.cause ?? '?'}) marked ${d.status ?? '?'}.`;
    case 'note':       return `${t}${retro} — Note: ${d.text ?? ''}`;
  }
}

export interface Code99NarrativeBundle {
  /** Human-readable draft narrative, ready to paste into the ePCR. */
  draft: string;
  /** The structured compliance footer appended after the prose. */
  complianceFooter: string;
  /** The raw prompt we'd send to the Claude API if we want enrichment. */
  apiPrompt: string;
}

export function buildCode99Narrative(
  session: Code99Session,
  events: Code99Event[],
): Code99NarrativeBundle {
  const ordered = events.slice().sort((a, b) =>
    a.event_time.localeCompare(b.event_time),
  );

  // -----------------------------
  // Draft narrative (deterministic)
  // -----------------------------
  const lines: string[] = [];

  // Opening
  lines.push('CARDIAC ARREST / CODE 99 RESUSCITATION LOG');
  lines.push('');
  if (session.arrest_time) {
    lines.push(`Arrest time: ${tstr(session.arrest_time)} (source: ${session.arrest_time_source ?? 'unspecified'}).`);
  } else {
    lines.push('Arrest time: not documented.');
  }
  lines.push(`Resuscitation activated at ${tstr(session.activated_at)}.`);
  if (session.cpr_started_at) {
    lines.push(`CPR initiated at ${tstr(session.cpr_started_at)}.`);
    if (session.arrest_time) {
      lines.push(`Down-time to CPR: ${durationBetween(session.arrest_time, session.cpr_started_at)}.`);
    }
  }
  lines.push('');

  // Chronology
  lines.push('CHRONOLOGY');
  if (ordered.length === 0) {
    lines.push('(no events logged)');
  } else {
    for (const e of ordered) lines.push(describeEvent(e));
  }
  lines.push('');

  // Outcome
  lines.push('OUTCOME');
  if (session.disposition === 'rosc') {
    lines.push('Return of spontaneous circulation achieved. Post-ROSC care initiated.');
    if (session.post_rosc_12_lead_at) {
      lines.push(`Post-ROSC 12-lead at ${tstr(session.post_rosc_12_lead_at)}.`);
    }
    if (session.post_rosc_vitals_captured_at) {
      lines.push(`Post-ROSC vital set captured at ${tstr(session.post_rosc_vitals_captured_at)}.`);
    }
  } else if (session.disposition === 'termination') {
    if (session.med_cmd_physician_name) {
      lines.push(`Medical command contacted: ${session.med_cmd_physician_name}` +
        (session.med_cmd_physician_badge ? ` (badge ${session.med_cmd_physician_badge})` : '') +
        (session.med_cmd_contact_time ? ` at ${tstr(session.med_cmd_contact_time)}` : '') + '.');
    }
    if (session.time_of_death) {
      lines.push(`Resuscitation terminated. Time of death: ${tstr(session.time_of_death)}.`);
    } else {
      lines.push('Resuscitation terminated.');
    }
    if (session.family_notified) {
      lines.push(`Family notification: ${session.family_notified.replace(/_/g, ' ')}.`);
    }
    if (session.law_enforcement_on_scene) {
      lines.push('Law enforcement on scene at termination.');
    }
  } else if (session.disposition === 'transferred_care') {
    lines.push('Care transferred to receiving facility with resuscitation ongoing.');
  } else if (session.disposition === 'ongoing_on_transfer') {
    lines.push('Resuscitation ongoing at transfer of care.');
  } else {
    lines.push('Disposition: not yet set.');
  }

  const draft = lines.join('\n');

  // -----------------------------
  // Compliance footer
  // -----------------------------
  const shocks = ordered.filter((e) => e.event_type === 'shock').length;
  const epiCount = ordered.filter((e) => e.event_type === 'medication'
    && typeof e.details?.name === 'string'
    && (e.details.name as string).toLowerCase().startsWith('epi')).length;
  const rhythms = ordered.filter((e) => e.event_type === 'rhythm').length;
  const hsts = ordered.filter((e) =>
    e.event_type === 'reversible_cause' && e.details?.status === 'addressed',
  ).map((e) => e.details?.cause).filter(Boolean);

  const footer = [
    'PROTOCOL COMPLIANCE SUMMARY',
    `Rhythms logged: ${rhythms}`,
    `Defibrillations: ${shocks}`,
    `Epinephrine doses: ${epiCount}`,
    `Reversible causes addressed: ${hsts.length > 0 ? hsts.join(', ') : 'none documented'}`,
    `Disposition: ${session.disposition ?? 'not set'}`,
  ].join('\n');

  // -----------------------------
  // API prompt (optional Claude enrichment)
  // -----------------------------
  const apiPrompt = [
    'You are generating a clinical narrative for a pre-hospital cardiac arrest resuscitation.',
    'Write in professional past-tense EMS prose. Every fact must come from the provided event log — do not invent rhythms, times, or interventions.',
    'Reference specific timestamps where relevant. Refer to the patient only as "the patient".',
    'Return the narrative as 3-6 paragraphs in order: (1) dispatch and arrival; (2) initial presentation and rhythm; (3) chronological interventions; (4) response to treatment; (5) outcome / transfer / termination; (6) optional brief summary.',
    '',
    'SESSION METADATA:',
    JSON.stringify(session, null, 2),
    '',
    'EVENT LOG (chronological):',
    ...ordered.map(describeEvent),
  ].join('\n');

  return { draft, complianceFooter: footer, apiPrompt };
}
